import { Component, Input, Output, EventEmitter } from '@angular/core';
import { MenuItem } from 'primeng/api';

import { TableComponent } from '../Table/table.component';
import { getProvider } from '../../models/control-value-accessor.model';
import { ActionKeys } from '../../enums/keys.enum';
import { TableColumnModel } from '../../models/table-column.model';
import { DisplayGroupTypeEnum } from '../../../../api/displayGroups/enums/display-group-type.enum';

export enum RowTypesEnum {
    group,
    row
}

export interface GroupedRow {
    id: number;
    name: string;
    rows: any[];
    selected: boolean;
    partiallySelected: boolean;
    editDisabled: boolean;
    actions?: MenuItem[];
    type: DisplayGroupTypeEnum;
}

export interface TableRow {
    id: number | DisplayGroupTypeEnum;
    type: RowTypesEnum;
    row?: any;
    group?: GroupedRow;
    expanded: boolean;
}

@Component({
    selector: 'agpr-grouped-table',
    styleUrls: ['../Table/table.component.scss', './grouped-table.component.scss'],
    templateUrl: './grouped-table.component.html',
    providers: [getProvider(GroupedTableComponent)],
    standalone: false
})
export class GroupedTableComponent extends TableComponent {
    @Input() groupNameMaxLength: number = 64;
    // Whether to show the group header if only one group is present
    @Input() showGroupHeaders: boolean = true;

    @Output() groupChanged: EventEmitter<GroupedRow> = new EventEmitter<GroupedRow>();

    rawData: GroupedRow[] = [];
    sortedData: GroupedRow[] = [];
    flattenedData: TableRow[];

    editingGroup: GroupedRow;

    readonly rowTypes = RowTypesEnum;

    public toggleGroupSelect(group: GroupedRow) {
        group.partiallySelected = false;
        group.rows.forEach(row => {
            row.selected = group.selected;
        });
        this.onChange(this.rawData);
    }

    public toggleGroup(row: TableRow) {
        row.expanded = !row.expanded;
        const items = this.flattenedData.filter(item => item.id === row.id);
        items.forEach(item => item.expanded = row.expanded);
    }

    public editGroupName(group: GroupedRow) {
        this.editingGroup = group;
    }

    public saveGroupName(event, group: GroupedRow) {
        if ((event instanceof KeyboardEvent && event.key === ActionKeys.ENTER) || event instanceof FocusEvent) {
            this.editingGroup = null;
            this.groupChanged.emit(group);
        }
    }

    public sort() {
        this.sortedData = (this.rawData || []).slice();

        if (!this.currentSortColumn) {
            this.setInitialFocus();
            return;
        }

        this.sortedData.forEach(group => {
            group.rows.sort((a, b) => {
                return this.compareRows(this.currentSortColumn, a , b);
            });
            if (!this.currentSortAscending) {
                group.rows = group.rows.reverse();
            }
        });

        this.flattenGroups();

        this.setInitialFocus();
    }

    private flattenGroups() {
        this.flattenedData = [];
        this.sortedData.forEach(group => {
            const id = !!group.id ? group.id : group.type;
            this.flattenedData.push({
                id: id,
                type: RowTypesEnum.group,
                group: group,
                expanded: true
            });
            this.flattenedData.push(
                ...group.rows.map((item) => {
                    return {
                        id: id,
                        type: RowTypesEnum.row,
                        row: item,
                        expanded: true
                    };
                })
            );
        });
    }

    public saveGroupedCheckboxValue(checked: boolean, column: TableColumnModel, row: TableRow): void {
        const group = this.sortedData.find(displayGroup => row.id === displayGroup.id || row.id === displayGroup.type);
        super.saveCheckboxValue(checked, column, row.row);
        if (group.rows.every(x => x[column.field] === checked || x[column.selectField] === checked)) {
            group.selected = checked;
            group.partiallySelected = false;
        } else {
            group.partiallySelected = true;
        }
    }

    public toggleSelectAll(): void {
        this.sortedData.forEach(group => {
            group.rows.forEach(row => {
                row.selected = this.selectAll;
            });
        });
        super.toggleSelectAll();
    }
}
